import { Suspense, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type Me } from '../api';
import { useAuth, useMe } from '../auth';
import { Loading, useOnline } from './ui';

// A interface só esconde; quem bloqueia é o servidor (ACS-008).
export const can = (me: Me, key: string) => (me.permissions[key] ?? 'nenhum') !== 'nenhum';

// Menu principal (telas da seção 6 dos requisitos). Itens de etapas futuras mostram a etapa prevista, sem dados fictícios.
export const MENU: { to: string; label: string; stage?: string; permission?: string }[] = [
  { to: '/', label: 'Início' },
  { to: '/empresas', label: 'Empresas', permission: 'companies.edit' },
  { to: '/contatos', label: 'Contatos', permission: 'companies.edit' },
  { to: '/oportunidades', label: 'Oportunidades', permission: 'opportunities.edit' },
  { to: '/relacionamento', label: 'Relacionamento', permission: 'companies.edit' },
  { to: '/saude', label: 'Saúde dos clientes', permission: 'companies.edit' },
  { to: '/agenda', label: 'Agenda e atividades', permission: 'companies.edit' },
  { to: '/propostas', label: 'Propostas', permission: 'proposals.edit' },
  { to: '/contratos', label: 'Contratos', permission: 'contracts.draft' },
  { to: '/campanhas', label: 'Campanhas', permission: 'campaigns.create' },
  { to: '/atendimento', label: 'Atendimento', permission: 'support.edit' },
  { to: '/carteira', label: 'Carteira contratual', permission: 'contracts.draft' },
  { to: '/faturamento', label: 'Fila de faturamento', permission: 'contracts.draft' },
  { to: '/relatorios', label: 'Relatórios', permission: 'reports.view' },
  { to: '/auditoria', label: 'Auditoria', permission: 'audit.view' },
  { to: '/configuracoes', label: 'Configurações' },
];

const ENV_LABEL: Record<string, string> = { desenvolvimento: 'AMBIENTE DE DESENVOLVIMENTO', validacao: 'AMBIENTE DE VALIDAÇÃO' };

export function Layout() {
  const me = useMe();
  const { logout } = useAuth();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.get<{ environment: string }>('/health'), staleTime: Infinity });
  const envLabel = health.data ? ENV_LABEL[health.data.environment] : undefined;
  // Sino: alertas internos não lidos (atualiza a cada minuto).
  const bell = useQuery({ queryKey: ['alerts-summary'], queryFn: () => api.get<{ open: number; unread: number }>('/alerts/summary'), refetchInterval: 60_000, enabled: online });
  const bellCount = bell.data?.unread ?? 0;

  useEffect(() => setMenuOpen(false), [location.pathname]);

  return (
    <div className="app">
      <a className="skip-link" href="#conteudo">
        Pular para o conteúdo
      </a>
      <aside className={`sidebar${menuOpen ? ' open' : ''}`} aria-label="Menu principal">
        <div className="brand">
          TECHNOVE
          <small>CRM</small>
        </div>
        <nav className="nav">
          {MENU.filter((m) => !m.permission || can(me, m.permission)).map((m) => (
            <NavLink key={m.to} to={m.to} end={m.to === '/'}>
              <span>{m.label}</span>
              {m.stage && <span className="stage" title={`Disponível na etapa ${m.stage}`}>{m.stage}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div>
            Conectado como <strong>{me.name}</strong>
            <div className="small muted">{me.roleName}</div>
          </div>
          <button className="btn btn-sm" onClick={() => void logout()}>
            Sair
          </button>
        </div>
      </aside>
      {menuOpen && <div className="dialog-backdrop show-mobile" style={{ zIndex: 35 }} onClick={() => setMenuOpen(false)} />}

      <div className="main">
        {envLabel && (
          <div className="env-banner" role="note">
            {envLabel} — dados de teste; nenhuma mensagem ou cobrança real
          </div>
        )}
        <header className="topbar">
          <button className="btn btn-ghost btn-sm show-mobile" onClick={() => setMenuOpen(true)} aria-label="Abrir menu">
            ☰ Menu
          </button>
          <span className="who">
            <strong>{me.name}</strong> · {me.roleName}
          </span>
          <span className="actions" style={{ alignItems: 'center' }}>
            <Link to="/alertas" className={`bell${bellCount ? ' has' : ''}`} aria-label={bellCount ? `${bellCount} alerta(s) não lido(s)` : 'Alertas'}>
              <span aria-hidden>🔔</span>
              {bellCount > 0 && <span className="bell-count">{bellCount > 99 ? '99+' : bellCount}</span>}
            </Link>
            <span className={`conn ${online ? 'online' : 'offline'}`} role="status" aria-live="polite">
              {online ? 'Conectado' : 'Sem conexão'}
            </span>
          </span>
        </header>
        <main className="content" id="conteudo" tabIndex={-1}>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Atalhos">
        <NavLink to="/" end>
          <span className="ico" aria-hidden>⌂</span>Início
        </NavLink>
        <NavLink to="/oportunidades">
          <span className="ico" aria-hidden>▤</span>Funil
        </NavLink>
        <NavLink to="/agenda">
          <span className="ico" aria-hidden>▦</span>Agenda
        </NavLink>
        <NavLink to="/atendimento">
          <span className="ico" aria-hidden>✆</span>Atendimento
        </NavLink>
        <button onClick={() => setMenuOpen(true)}>
          <span className="ico" aria-hidden>⋯</span>Mais
        </button>
      </nav>
    </div>
  );
}
